import React, { useEffect, useState } from "react";
import { Check, Package, Loader2, ShoppingBag, Sparkles } from "lucide-react";
import { fetchKits } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

export const KitsSection = () => {
    const [kits, setKits] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addItem } = useCart();

    useEffect(() => {
        fetchKits()
            .then(setKits)
            .catch((e) => {
                console.error(e);
                toast.error("Não foi possível carregar os kits");
            })
            .finally(() => setLoading(false));
    }, []);

    const addKit = (k) => {
        addItem({ id: k.id, name: k.name, price: k.price, image: k.image, color: k.color });
        toast.success(`${k.name} adicionado ao carrinho`);
    };

    return (
        <section
            id="kits"
            data-testid="kits-section"
            className="py-20 md:py-28 relative overflow-hidden bg-brand-cream"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-2.png')" }}
            />
            <div
                aria-hidden
                className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-green/15 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-brand-orange/20 blur-3xl"
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            <Package className="h-3.5 w-3.5" />
                            Kits completos
                        </span>
                        <h2 className="mt-3 font-display font-medium text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight">
                            Leva tudo para{" "}
                            <span className="text-brand-green">começar hoje.</span>
                        </h2>
                        <p className="mt-4 text-lg text-brand-ink-soft">
                            Liquidificador, adaptador, copos de marca e polpa congelada. Tudo
                            numa caixa. Chega, abre e prepara.
                        </p>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-brand-ink-soft bg-white rounded-full px-4 py-2 shadow-soft">
                        <Sparkles className="h-4 w-4 text-brand-orange" />
                        Envio em 48h
                    </div>
                </div>

                {loading ? (
                    <div className="mt-16 flex justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
                    </div>
                ) : (
                    <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
                        {kits.map((k) => {
                            const highlighted = k.id === "kit-familia";
                            return (
                                <article
                                    key={k.id}
                                    data-testid={`kit-card-${k.id}`}
                                    className={`group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                                        highlighted
                                            ? "bg-brand-ink text-white shadow-pop md:-translate-y-4 ring-2 ring-brand-orange"
                                            : "bg-white text-brand-ink shadow-soft hover:shadow-pop ring-1 ring-brand-green/10"
                                    }`}
                                >
                                    {k.badge && (
                                        <div
                                            className={`absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold shadow-pop ${
                                                highlighted
                                                    ? "bg-brand-orange text-white"
                                                    : "bg-brand-green text-white"
                                            }`}
                                        >
                                            {k.badge}
                                        </div>
                                    )}
                                    <div
                                        className="relative h-52 md:h-60 overflow-hidden"
                                        style={{ background: k.color + "30" }}
                                    >
                                        <img
                                            src={k.image}
                                            alt={k.name}
                                            className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                        <div
                                            className={`absolute inset-0 ${
                                                highlighted
                                                    ? "bg-gradient-to-t from-brand-ink/70 to-transparent"
                                                    : "bg-gradient-to-t from-white/40 to-transparent"
                                            }`}
                                        />
                                    </div>

                                    <div className="p-6 md:p-8 flex-1 flex flex-col">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <h3 className="font-display font-medium text-2xl md:text-3xl leading-tight">
                                                    {k.name}
                                                </h3>
                                                <p
                                                    className={`mt-1 text-sm ${highlighted ? "text-white/80" : "text-brand-ink-soft"}`}
                                                >
                                                    {k.tagline}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-5 flex items-baseline gap-2">
                                            <span className="font-display font-medium text-4xl md:text-5xl">
                                                {Math.round(k.price)} €
                                            </span>
                                            <span
                                                className={`text-sm ${highlighted ? "text-white/60" : "text-brand-ink-soft"}`}
                                            >
                                                / kit
                                            </span>
                                        </div>

                                        <div
                                            className={`mt-4 inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-xs font-semibold ${
                                                highlighted
                                                    ? "bg-white/10 text-white"
                                                    : "bg-brand-green-soft text-brand-green"
                                            }`}
                                        >
                                            <span>≈ {k.yield_cups} copos</span>
                                        </div>

                                        <ul className="mt-6 space-y-3 flex-1">
                                            {k.includes.map((line) => (
                                                <li
                                                    key={line}
                                                    className="flex items-start gap-2.5 text-sm"
                                                >
                                                    <span
                                                        className={`mt-0.5 shrink-0 h-5 w-5 rounded-full flex items-center justify-center ${
                                                            highlighted
                                                                ? "bg-brand-orange/20 text-brand-orange"
                                                                : "bg-brand-green-soft text-brand-green"
                                                        }`}
                                                    >
                                                        <Check className="h-3 w-3" />
                                                    </span>
                                                    <span
                                                        className={
                                                            highlighted
                                                                ? "text-white/90"
                                                                : "text-brand-ink-soft"
                                                        }
                                                    >
                                                        {line}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>

                                        <button
                                            type="button"
                                            onClick={() => addKit(k)}
                                            data-testid={`add-kit-${k.id}`}
                                            className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                                                highlighted
                                                    ? "bg-brand-orange hover:bg-brand-orange-dark text-white shadow-pop"
                                                    : "bg-brand-green hover:bg-brand-green-dark text-white shadow-soft"
                                            }`}
                                        >
                                            <ShoppingBag className="h-4 w-4" />
                                            Adicionar ao carrinho
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                <div className="mt-10 rounded-2xl bg-white ring-1 ring-brand-green/10 px-6 py-5 text-center shadow-soft">
                    <p className="text-sm text-brand-ink-soft">
                        Precisas de algo à medida (hotelaria, ginásios, eventos)?{" "}
                        <a
                            href="#contacto"
                            className="font-bold text-brand-green hover:underline"
                            data-testid="kits-custom-link"
                        >
                            Fala connosco
                        </a>{" "}
                        — montamos um pacote personalizado.
                    </p>
                </div>
            </div>
        </section>
    );
};
